import { Space } from '@procurify/ui'
import { cloneDeep } from 'lodash'

import { type IBudgetCategoryAttributeDescription } from '@webapp/components/Budgets/BudgetCategoryAttributeDescriptions'
import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import { type BudgetFormat } from '@webapp/hooks/useFormatBudget'
import { type BudgetIntervalType } from '@webapp/models/enums/BudgetInterval'
import { BudgetCategoryCard } from '../BudgetCategoryCard'
import { BudgetTrackerCardStyled } from './styles/BudgetTrackerCardStyled'

export interface IBudgetTrackerCardProps {
  name: string
  startDate: Date
  endDate: Date
  isSourceOfTruth: boolean
  timeZoneTooltip: TimezonesEnum
  budgetSpendDetails: BudgetFormat
  shouldRenderPercentagesFirst?: boolean
  shouldRenderOverageTagsBelowText?: boolean
  departments?: string[]
  accountCodes?: string[]
  customFieldAttributes?: IBudgetCategoryAttributeDescription[]
  period?: Omit<BudgetIntervalType, 'none'> | null
  showPercentagesOnlyForSummedTotals?: boolean
}

export const BudgetTrackerCard = ({
  name,
  startDate,
  endDate,
  isSourceOfTruth,
  departments,
  accountCodes,
  customFieldAttributes,
  budgetSpendDetails,
  period,
  shouldRenderOverageTagsBelowText = true,
  shouldRenderPercentagesFirst = true,
  showPercentagesOnlyForSummedTotals = false,
}: IBudgetTrackerCardProps) => {
  const {
    budgetType,
    isOverBudget,
    total,
    committed,
    approved,
    billed,
    received,
    purchased,
    spendRequest,
    remaining,
    pending,
    remainingMinusPending,
    spendTotal,
  } = budgetSpendDetails

  const isFixedBudget = budgetType === 'fixed'
  const newTotal = cloneDeep(total)
  const newCommitted = cloneDeep(committed)
  const newPending = cloneDeep(pending)
  const newSpendRequest = cloneDeep(spendRequest)

  if (isFixedBudget && showPercentagesOnlyForSummedTotals) {
    newTotal.percentage.amount = undefined
    newCommitted.percentage.amount = undefined
    newPending.percentage.amount = undefined

    if (newSpendRequest?.percentage) {
      newSpendRequest.percentage.amount = undefined
    }
  }

  if (!startDate && !endDate) {
    return (
      <BudgetTrackerCardStyled>
        <BudgetCategoryCard>
          <Space size='medium'>
            <BudgetCategoryCard.Header
              name={name}
              startDate={startDate}
              endDate={endDate}
              shouldShowBudgetPeriod
              period={period}
              departments={departments}
              accountCodes={accountCodes}
              customFieldAttributes={customFieldAttributes}
              isSourceOfTruth={isSourceOfTruth}
            />
            <BudgetCategoryCard.EmptyState />
          </Space>
        </BudgetCategoryCard>
      </BudgetTrackerCardStyled>
    )
  }

  return (
    <BudgetTrackerCardStyled>
      <BudgetCategoryCard>
        <Space size='medium'>
          <BudgetCategoryCard.Header
            name={name}
            startDate={startDate}
            endDate={endDate}
            shouldShowBudgetPeriod
            period={period}
            departments={departments}
            accountCodes={accountCodes}
            customFieldAttributes={customFieldAttributes}
            isSourceOfTruth={isSourceOfTruth}
          />

          {isFixedBudget && (
            <BudgetCategoryCard.ProgressBar
              isOverBudget={isOverBudget}
              committedSpend={committed}
              pendingRequests={pending}
              remainingAfterPendingRequests={remainingMinusPending}
              totalSpend={spendTotal}
              spendRequest={spendRequest}
            />
          )}

          <BudgetCategoryCard.Subtotals
            isOverBudget={isOverBudget}
            totalBudget={newTotal}
            totalSpend={spendTotal}
            committedSpend={newCommitted}
            spendRequest={newSpendRequest}
            remaining={remaining}
            pendingRequests={newPending}
            remainingAfterPendingRequests={remainingMinusPending}
            approvedSpend={approved}
            billedSpend={billed}
            receivedSpend={received}
            purchasedSpend={purchased}
            shouldRenderPercentagesFirst={shouldRenderPercentagesFirst}
            shouldRenderOverageTagsBelowText={shouldRenderOverageTagsBelowText}
          />
        </Space>
      </BudgetCategoryCard>
    </BudgetTrackerCardStyled>
  )
}
